class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();

    // éléments “non critiques”
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  // Getters utilitaires
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }
  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  // Attacher les events quand le composant est dans le DOM
  connectedCallback() {
    const inputEl = this.input;
    const resetEl = this.resetButton;
    const typeEl = this.searchProductTypes;

    if (resetEl) {
      resetEl.addEventListener('click', this.clear.bind(this));
    } else {
      console.warn('[predictive-search] reset button not found');
    }

    if (inputEl) {
      this.focusElement = inputEl;
      inputEl.addEventListener('input', customTheme.utils.debounce(this.onChange.bind(this), 300));
      inputEl.addEventListener('focus', this.onFocus.bind(this));
    } else {
      console.warn('[predictive-search] input[type="search"] not found');
    }

    typeEl?.addEventListener('change', this.handleProductTypeChange.bind(this));
  }

  // Nettoyage si l’élément est retiré du DOM
  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    const inputEl = this.input;
    const resetEl = this.resetButton;
    const typeEl = this.searchProductTypes;

    if (inputEl) {
      inputEl.removeEventListener('input', this.onChange);
      inputEl.removeEventListener('focus', this.onFocus);
    }
    resetEl?.removeEventListener('click', this.clear);
    typeEl?.removeEventListener('change', this.handleProductTypeChange);
  }

  onFocus(event) {
    // Protéger si pas de header
    const headerBottom = this.header?.getBoundingClientRect
      ? parseInt(this.header.getBoundingClientRect().bottom)
      : 0;

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);

    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
    }
    if (!this.searchRecommendationEmpty) {
      this.searchContent?.classList.remove('hidden');
    }

    this.classList.add('predictive-search-open');

    if (this.getQuery().length === 0) return;

    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  clear(event = null) {
    event?.preventDefault();
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${customTheme.routes.shop_url}${customTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value !== '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    url.searchParams.set('q', search_term);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', customTheme.utils.getSectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + customTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (!targetElement) {
      console.error(`Element with id '${id}' not found in the document.`);
      return;
    }

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);

    if (contentElement) {
      this.searchContent?.classList.remove('hidden');
      targetElement.innerHTML = contentElement.innerHTML;

      if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
        document.body.classList.add('search-open');
        this.classList.add('predictive-search-open');
      }
    } else {
      console.error(`Element with id '${id}' not found in the parsed response.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
